/** @format */
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    InputSignal,
    Signal,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { StudyPeriodLocator } from 'common-typescript/types';
import {
    SelectCourseUnitsModalComponent,
    selectCourseUnitsModalOpener,
    SelectCourseUnitsModalValues,
} from '../../select-course-units-modal/select-course-units-modal.component';
import { isTimelineCourseUnit, isTimelineCustomCourseUnitAttainment, isTimelineCustomStudyDraft } from '../../timing.types';
import {
    GridArea,
    HorizontalTimelineData,
    HorizontalTimelineService,
    PositionedStudyPeriodData,
    PositionedTimelineItem,
} from './horizontal-timeline.service';

type TimelineStudyTermHeadingLevel = 3 | 4;
type TimelineStudyPeriodHeadingLevel = 4 | 5;
type TimelineStudyPeriodItemHeadingLevel = 5 | 6;

const MIRRORED_FOCUS_CLASS = 'app-horizontal-timeline__track__timeline-item__mirrored-focus';
const FOCUSABLE_ELEMENTS_SELECTOR = 'a[href], button, input, select, textarea, [tabindex]';

interface StudyPeriodTimelineItem {
    readonly item: PositionedTimelineItem;
    /** True for the hidden copy of an item that starts in a previous study period */
    readonly isContinuee: boolean;
}

/**
 * Horizontally scrollable alternative to the vertical timeline in {@link TimelineComponent}.
 */
@Component({
    selector: 'app-horizontal-timeline',
    standalone: false,
    templateUrl: './horizontal-timeline.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [HorizontalTimelineService],
})
export class HorizontalTimelineComponent {
    private readonly viewContainerRef = inject(ViewContainerRef);
    private readonly hostElement: HTMLElement = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    /** The element of a visible timing that shows the focus of a hidden copy of the timing */
    private mirroredFocusElement: Element | null = null;
    private readonly selectCourseUnitsModalOpener: (
        data: SelectCourseUnitsModalValues,
        viewContainerRef: ViewContainerRef,
    ) => MatDialogRef<SelectCourseUnitsModalComponent, null> = selectCourseUnitsModalOpener();

    /** Id of the visible heading that names the scrollable region for assistive technologies. */
    readonly labelledBy: InputSignal<string> = input.required<string>();
    readonly studyTermHeadingLevel: InputSignal<TimelineStudyTermHeadingLevel> = input.required<TimelineStudyTermHeadingLevel>();

    protected readonly studyPeriodHeadingLevel: Signal<TimelineStudyPeriodHeadingLevel> = computed(
        () => <TimelineStudyPeriodHeadingLevel>(this.studyTermHeadingLevel() + 1),
    );
    protected readonly studyPeriodItemHeadingLevel: Signal<TimelineStudyPeriodItemHeadingLevel> = computed(
        () => <TimelineStudyPeriodItemHeadingLevel>(this.studyTermHeadingLevel() + 2),
    );

    protected readonly timelineData: Signal<HorizontalTimelineData> = toSignal(inject(HorizontalTimelineService).positionedStudyTerms$, {
        initialValue: {
            studyTerms: [],
            studyPeriods: [],
            timelineItemsByStartingPeriod: new Map(),
            timelineItemsByContinueePeriod: new Map(),
        },
    });

    /**
     * The timeline items of each study period in the order of their rows, so that the DOM order matches the visual order for screen
     * readers. Includes the hidden copies of the items continuing from a previous study period.
     */
    protected readonly timelineItemsByStudyPeriod: Signal<ReadonlyMap<StudyPeriodLocator, readonly StudyPeriodTimelineItem[]>> = computed(
        () => {
            const { timelineItemsByStartingPeriod, timelineItemsByContinueePeriod } = this.timelineData();
            const timelineItemsByStudyPeriod = new Map<StudyPeriodLocator, readonly StudyPeriodTimelineItem[]>();
            timelineItemsByStartingPeriod.forEach((startingItems, locator) => {
                const continueeItems = timelineItemsByContinueePeriod.get(locator) ?? [];
                timelineItemsByStudyPeriod.set(
                    locator,
                    [
                        ...startingItems.map(item => ({ item, isContinuee: false })),
                        ...continueeItems.map(item => ({ item, isContinuee: true })),
                    ].sort((a, b) => a.item.rowStart - b.item.rowStart),
                );
            });
            return timelineItemsByStudyPeriod;
        },
    );

    protected gridAreaStyle({ rowStart, columnStart, rowEnd, columnEnd }: GridArea): { gridArea: string } {
        return { gridArea: `${rowStart} / ${columnStart} / ${rowEnd} / ${columnEnd}` };
    }

    protected openSelectCourseUnitsModal(studyPeriodData: PositionedStudyPeriodData): void {
        this.selectCourseUnitsModalOpener({ studyPeriodData }, this.viewContainerRef);
    }

    /** Identifies the visible timing, so that the focus of its hidden copies can be shown on it */
    protected timelineItemKey({ id, columnStart }: PositionedTimelineItem): string {
        return `${id}/${columnStart}`;
    }

    /**
     * The hidden copies of a timing in the study periods where it continues can be focused, but their focus is not visible.
     * Shows the focus on the corresponding element of the visible timing instead. The actual focus stays on the hidden copy.
     */
    protected showFocusOnVisibleTimelineItem(event: FocusEvent, item: PositionedTimelineItem): void {
        const focusedElementIndex = getFocusableElements(<Element>event.currentTarget).indexOf(<Element>event.target);
        const visibleTimelineItem = this.hostElement.querySelector(`[data-timeline-item-key="${CSS.escape(this.timelineItemKey(item))}"]`);
        this.mirroredFocusElement = visibleTimelineItem ? (getFocusableElements(visibleTimelineItem)[focusedElementIndex] ?? null) : null;
        this.mirroredFocusElement?.classList.add(MIRRORED_FOCUS_CLASS);
    }

    protected hideFocusOnVisibleTimelineItem(): void {
        this.mirroredFocusElement?.classList.remove(MIRRORED_FOCUS_CLASS);
        this.mirroredFocusElement = null;
    }

    protected readonly isTimelineCourseUnit = isTimelineCourseUnit;
    protected readonly isTimelineCustomCourseUnitAttainment = isTimelineCustomCourseUnitAttainment;
    protected readonly isTimelineCustomStudyDraft = isTimelineCustomStudyDraft;
}

function getFocusableElements(element: Element): Element[] {
    return Array.from(element.querySelectorAll(FOCUSABLE_ELEMENTS_SELECTOR));
}
